import * as React from "react";
import "./inviteGallery.css";

const INVITE_IMAGES = [
    {
        src: "https://storage.googleapis.com/rsvpfiles/jedtonet/1.png",
        alt: "Wedding invitation, first page",
    },
    {
        src: "https://storage.googleapis.com/rsvpfiles/jedtonet/2.png",
        alt: "Wedding invitation, second page",
    },
    {
        src: "https://storage.googleapis.com/rsvpfiles/jedtonet/3.png",
        alt: "Wedding invitation, third page",
    },
    {
        src: "https://storage.googleapis.com/rsvpfiles/jedtonet/4.png",
        alt: "Wedding invitation, fourth page",
    },
    {
        src: "https://storage.googleapis.com/rsvpfiles/jedtonet/5.png",
        alt: "Wedding invitation, fifth page",
    },
];

const NAV_BUTTON = 44;
const NAV_INSET = 20;

function InviteGallery() {
    const scrollerRef = React.useRef(null);
    const layerRefs = React.useRef([]);
    const navStateRef = React.useRef({ canUp: false, canDown: true, overImage: false });
    const [nav, setNav] = React.useState(navStateRef.current);

    const scrollPage = (direction) => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return;
        }
        const page = scroller.clientHeight || 1;
        const index = Math.round(scroller.scrollTop / page);
        const last = Math.max(INVITE_IMAGES.length - 1, 0);
        const target = Math.min(Math.max(index + direction, 0), last);
        scroller.scrollTo({
            top: target * page,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        });
    };

    React.useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) {
            return undefined;
        }

        const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        let frame = 0;

        const updateNav = () => {
            const loaded = layerRefs.current.filter((node) => node && node.naturalWidth);
            const total = Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
            let overImage = false;

            if (loaded.length) {
                const imageWidth = Math.max(...loaded.map((node) => node.getBoundingClientRect().width));
                const gutter = (scroller.clientWidth - imageWidth) / 2;
                overImage = gutter < NAV_INSET + NAV_BUTTON;
            }

            const next = {
                canUp: scroller.scrollTop > 12,
                canDown: scroller.scrollTop < total - 12,
                overImage,
            };

            const prev = navStateRef.current;
            if (prev.canUp === next.canUp && prev.canDown === next.canDown && prev.overImage === next.overImage) {
                return;
            }
            navStateRef.current = next;
            setNav(next);
        };

        const paint = () => {
            const layers = layerRefs.current.filter(Boolean);
            const count = layers.length;
            updateNav();
            if (!count) {
                return;
            }

            const total = Math.max(scroller.scrollHeight - scroller.clientHeight, 1);
            const scrolled = Math.min(Math.max(scroller.scrollTop, 0), total);
            const exact = (scrolled / total) * (count - 1);
            const reduced = reducedQuery.matches;

            layers.forEach((img, index) => {
                let opacity = 0;
                let zIndex = 0;

                if (reduced || count === 1) {
                    const active = Math.round(exact);
                    opacity = index === active ? 1 : 0;
                    zIndex = opacity ? 2 : 0;
                } else {
                    const base = Math.min(Math.floor(exact + 1e-6), count - 2);
                    const frac = Math.min(Math.max(exact - base, 0), 1);
                    if (index === base) {
                        opacity = 1;
                        zIndex = 1;
                    } else if (index === base + 1) {
                        opacity = frac;
                        zIndex = 2;
                    }
                }

                img.style.opacity = opacity.toFixed(3);
                img.style.transform = "translateX(-50%)";
                img.style.zIndex = String(zIndex);
            });
        };

        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(paint);
        };

        const onImageLoad = () => updateNav();
        layerRefs.current.forEach((img) => {
            if (img) {
                img.addEventListener("load", onImageLoad);
            }
        });

        paint();
        scroller.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        if (reducedQuery.addEventListener) {
            reducedQuery.addEventListener("change", onScroll);
        }

        return () => {
            cancelAnimationFrame(frame);
            scroller.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            layerRefs.current.forEach((img) => {
                if (img) {
                    img.removeEventListener("load", onImageLoad);
                }
            });
            if (reducedQuery.removeEventListener) {
                reducedQuery.removeEventListener("change", onScroll);
            }
        };
    }, []);

    React.useEffect(() => {
        const scroller = scrollerRef.current;
        const firstPage = layerRefs.current[0];
        if (!scroller || !firstPage || INVITE_IMAGES.length < 2) {
            return undefined;
        }

        let timer = 0;

        const advance = () => {
            if (scroller.scrollTop > 12) {
                return;
            }
            const page = scroller.clientHeight || 1;
            const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            scroller.scrollTo({
                top: page,
                behavior: reduced ? "auto" : "smooth",
            });
        };

        const arm = () => {
            if (timer) {
                return;
            }
            timer = window.setTimeout(advance, 2000);
        };

        if (firstPage.complete && firstPage.naturalWidth) {
            arm();
        } else {
            firstPage.addEventListener("load", arm);
        }

        return () => {
            window.clearTimeout(timer);
            firstPage.removeEventListener("load", arm);
        };
    }, []);

    return (
        <section className="invite-scroller" ref={scrollerRef} aria-label="Wedding invitation">
            <div className="invite-track">
                <div className="invite-viewport">
                    {INVITE_IMAGES.map((image, index) => (
                        <img
                            key={image.src}
                            ref={(node) => {
                                layerRefs.current[index] = node;
                            }}
                            className="invite-layer"
                            src={image.src}
                            alt={image.alt}
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                            draggable="false"
                        />
                    ))}
                    {nav.canUp && (
                        <button
                            type="button"
                            className={`invite-scroll-btn invite-scroll-btn--up${nav.overImage ? " invite-scroll-btn--over" : ""}`}
                            aria-label="Scroll up"
                            onClick={() => scrollPage(-1)}
                        >
                            <ScrollChevron direction="up" />
                        </button>
                    )}
                    {nav.canDown && (
                        <button
                            type="button"
                            className={`invite-scroll-btn invite-scroll-btn--down${nav.overImage ? " invite-scroll-btn--over" : ""}`}
                            aria-label="Scroll down"
                            onClick={() => scrollPage(1)}
                        >
                            <ScrollChevron direction="down" />
                        </button>
                    )}
                </div>
                {INVITE_IMAGES.map((image) => (
                    <div className="invite-snap" key={image.src} aria-hidden="true" />
                ))}
            </div>
        </section>
    );
}

function ScrollChevron({ direction }) {
    const points = direction === "up" ? "6 14.5 12 8.5 18 14.5" : "6 9.5 12 15.5 18 9.5";
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <polyline
                points={points}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default InviteGallery;
